// hooks/useNotificationCategoryList.ts

import { fetchPostByCategories } from "@/services/postServices"
import { getSiteIdFromHost } from "@/sites/config"
import { Meta, PostData } from "@/types/types"
import { useCallback, useEffect, useMemo, useState } from "react"

interface UseNotificationCategoryListProps {
  categoryId: number
  initialPosts?: PostData[]
  initialMeta?: Meta
  initialPage?: number
  locale: string
  pageSize?: number
}

interface UseNotificationCategoryListResult {
  posts: PostData[]
  meta: Meta
  loading: boolean
  currentPage: number
  totalPages: number
  setCurrentPage: (page: number) => void
  refresh: () => void
}

export const useNotificationCategoryList = ({
  categoryId,
  initialPosts = [],
  initialMeta = { pagination: { pageSize: 10, total: 0, current: 1 } },
  initialPage = 1,
  locale,
  pageSize = 10,
}: UseNotificationCategoryListProps): UseNotificationCategoryListResult => {
  const [posts, setPosts] = useState<PostData[]>(initialPosts)
  const [meta, setMeta] = useState<Meta>(initialMeta)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [loading, setLoading] = useState(false)
  // `window` chỉ tồn tại ở client. Đọc thẳng trong render body sẽ ném ReferenceError
  // khi SSR/prerender (ISR) → 500. siteId chỉ dùng trong fetchPosts (chạy client),
  // nên giá trị fallback lúc SSR không bao giờ được dùng để fetch.
  const siteId = typeof window !== "undefined" ? getSiteIdFromHost(window.location.host) : "tamdamedia.eu"

  const fetchPosts = useCallback(
    async (page: number) => {
      setLoading(true)
      try {
        const response = await fetchPostByCategories([String(categoryId)], {
          page: String(page),
          pageSize: String(pageSize),
          locale,
          siteId
        })

        if (response?.data && response?.meta) {
          setPosts(response.data as PostData[])
          setMeta(response.meta)
        } else {
          setPosts([])
          setMeta({ pagination: { page, pageSize, total: 0, current: page } })
        }
      } catch (error) {
        console.error("Error fetching notification category posts:", error)
        setPosts([])
      } finally {
        setLoading(false)
      }
    },
    [categoryId, locale, pageSize, siteId],
  )

  useEffect(() => {
    // Nếu là trang đầu và có initial data từ SSR → không fetch lại
    if (initialPosts.length > 0 && currentPage === initialPage) {
      return
    }
    fetchPosts(currentPage)
  }, [currentPage, fetchPosts, initialPosts.length, initialPage])

  const totalPages = useMemo(() => {
    const total = meta.pagination?.total || 0
    const size = meta.pagination?.pageSize || pageSize
    return Math.max(1, Math.ceil(total / size))
  }, [meta.pagination?.total, meta.pagination?.pageSize, pageSize])

  const refresh = useCallback(() => {
    fetchPosts(currentPage)
  }, [currentPage, fetchPosts])

  return {
    posts,
    meta,
    loading,
    currentPage,
    totalPages,
    setCurrentPage,
    refresh,
  }
}
